import {
  selectAnalysisDeviceId, selectAnalysisFiltersWereSelected,
  selectAnalysisTechnologyId,
  selectDeviceListForD3,
  selectRangeWithUnits,
  setChannelsForD3,
  setDeviceId,
  setDeviceList,
  setTechnologyId,
  useTypedDispatch,
} from "../../../app/state";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { convertToHz } from "../../../helpers/converters/frequency";
import _ from "lodash";
import { Toolbar } from "../../../app/components";
import { Menu } from "../../../app/shared/menu/Menu";
import useDevicesPage from "../../DevicesPage/devicePageHooks";
import { selectDevicesByArrayOfIds, selectDevicesByMapId } from "../../../store/deviceStore/deviceSelectors";
import {
  selectAllTechnologyConfigurations
} from "../../../store/technologyConfigurationStore/technologyConfigurationSelectors";
import { useSnackbar } from "../../../app/shared/snackbar-context/snackbar.context";
import ChannelModel from "../../../models/ChannelModel";
import { rangeIntersection } from "../../../helpers/math";
import HorizontalPanel from "../../../components/HorizontalPanel";
import FrequencyAnalysisForm from "../FrequencyAnalysisForm";
import FrequencyAnalysisPage from "../../../pages/FrequencyAnalysisPage";

const checkChannel = (channel: ChannelModel, bandwidth = '0 Hz', range) => {
  const rawFrequencyFrom = convertToHz(range[0]);
  const rawFrequencyTo = convertToHz(range[1]);
  const { centralFrequencyHz } = channel;
  const bottomValue = centralFrequencyHz - convertToHz(bandwidth) / 2;
  const topValue = centralFrequencyHz + convertToHz(bandwidth) / 2;

  return !!rangeIntersection(rawFrequencyFrom, rawFrequencyTo, bottomValue, topValue);
}

const AnalysisRightPanel = () => {
  const dispatch = useTypedDispatch();
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();
  const { state } = useDevicesPage();
  const { currentMapId } = state;
  const isFiltersSelected = useSelector(selectAnalysisFiltersWereSelected);
  const currentPlaceDevices = useSelector(selectDevicesByMapId(currentMapId));
  const allTechnologies = useSelector(selectAllTechnologyConfigurations);
  const range = useSelector(selectRangeWithUnits);
  let filteredDevices = useSelector(selectDeviceListForD3);
  const selectedTechnologyId = useSelector(selectAnalysisTechnologyId);
  const selectedDeviceId = useSelector(selectAnalysisDeviceId);
  const localStorageDeviceIdList = FrequencyAnalysisPage.loadFromStorage();
  const localStorageDeviceList = useSelector(selectDevicesByArrayOfIds(localStorageDeviceIdList));
  const deviceList = currentMapId ? currentPlaceDevices : localStorageDeviceList;

  useEffect(() => {
    dispatch(setTechnologyId(0));
    dispatch(setDeviceId(0));
  }, []);

  const selectedTechnologyConfiguration = allTechnologies.find((technology) => technology.id === selectedTechnologyId);

  if (!isFiltersSelected) {
    filteredDevices = deviceList;
  }

  let devicesByTechnology = filteredDevices;

  if (selectedTechnologyConfiguration) {
    devicesByTechnology = devicesByTechnology.filter((device) => {
      return (device?.configuration?.wirelessInterfaces || []).some((wi) => wi.technologyConfigurationId === selectedTechnologyConfiguration?.id);
    });
  }

  const technologyIds: number[] = [];
  filteredDevices.forEach((device) => {
    (device?.configuration?.wirelessInterfaces || []).forEach((wi) => {
      if ((wi?.channels || []).some((channel) => checkChannel(channel, wi.technologyConfiguration?.bandwidth, range))) {
        const { technologyConfigurationId } = wi;
        const technologyConfiguration = allTechnologies.find((technology) => technology.id === technologyConfigurationId)

        if (technologyConfiguration) {
          technologyIds.push(technologyConfiguration.id)
        }
      }
    })
  });

  const filteredTechnologies = _.sortBy(allTechnologies.filter((technology) => {
    return technologyIds.includes(technology.id);
  }), 'id', 'asc');

  const onSelectRange = (range) => {
    const deviceList = currentMapId ? currentPlaceDevices : localStorageDeviceList;
    const filteredDevices = deviceList.filter((device) => {
      return (device?.configuration?.wirelessInterfaces || []).some((wi) => {
        return (wi?.channels || []).some((channel) => checkChannel(channel, wi.technologyConfiguration?.bandwidth, range))
      })
    });

    if (filteredDevices.length === 0) {
      openSnackbar({
        title: 'No devices found',
        message: 'No devices according selected filters',
        severity: 'warning',
      });
    }

    dispatch(setDeviceList(filteredDevices));

    const channels = [] as any;

    filteredDevices.forEach((device) => {
      (device?.configuration?.wirelessInterfaces || []).forEach((wi) => {
        (wi.channels || []).filter((channel) => checkChannel(channel, wi.technologyConfiguration?.bandwidth, range))
          .forEach((channel) => {
            channels.push({
              technologyConfigurationId: wi.technologyConfigurationId,
              centralFrequencyHz: channel.centralFrequencyHz,
              bandwidth: convertToHz(wi.technologyConfiguration?.bandwidth || '')
            })
          })
      })
    })

    const filtered = _.uniqWith(
      _.sortBy(
        channels,
        'technologyConfigurationId',
        'asc'
      ),
      _.isEqual
    );

    const channelsForD3 = filtered.map((item) => item.technologyConfigurationId)
      .filter((value, index, self) => self.indexOf(value) === index)
      .map((value) => {
        return {
          technologyConfigurationId: value,
          technologyName: allTechnologies.find((technology) => technology.id === value)?.name,
          channels: filtered.filter((channel) => channel.technologyConfigurationId === value).map((channel) => {
            return {
              centralFrequencyHz: channel.centralFrequencyHz,
              bandwidth: channel.bandwidth
            }
          })
        }
      });

    dispatch(setChannelsForD3(channelsForD3));
  }

  return (
    <HorizontalPanel size={HorizontalPanel.SIZES.WHOLE_SPACE}>
      <HorizontalPanel size={HorizontalPanel.SIZES.SMALL}>
        <Toolbar title={'Frequency Range'} />
      </HorizontalPanel>
      <HorizontalPanel size={HorizontalPanel.SIZES.SMALL}>
        <FrequencyAnalysisForm onSubmit={onSelectRange} />
      </HorizontalPanel>
      <HorizontalPanel size={HorizontalPanel.SIZES.SMALL}>
        <Toolbar title={t('Technologies')} />
        <Menu
          menuItems={filteredTechnologies}
          selectItem={(id) => dispatch(setTechnologyId(selectedTechnologyId === id ? 0 : id))}
          selectedItemId={selectedTechnologyId}
          triggerSelectOnTheSameId
        />
      </HorizontalPanel>
      <HorizontalPanel size={HorizontalPanel.SIZES.SMALL}>
        <Toolbar title={t('Devices')} />
        <Menu
          menuItems={devicesByTechnology}
          selectItem={(id) => dispatch(setDeviceId(id))}
          selectedItemId={selectedDeviceId}
          showDeviceIcon={true}
        />
      </HorizontalPanel>
    </HorizontalPanel>
  );
};

export default AnalysisRightPanel;